// Portfolio Administration v1.0 PR 6 — side-by-side comparison of two
// versions of a waterfall config. Used when E1 is reviewing a proposed
// version before approving.

import type { PriorityRow } from "@/api/portfolioAdmin/pr6Types";
import { cn } from "@/design-system/lib/utils";

interface VersionDiffProps {
  baseRows: PriorityRow[];
  proposedRows: PriorityRow[];
}

// WS3 — diff keys on the canonical executed-row fields. The legacy keying
// (recipientRoleSlot|basisType|basisValue|basisCap) returned
// "undefined|undefined|undefined|undefined" against the canonical
// PrioritiesJson so the added/removed counters always read 0.
function rowKey(r: PriorityRow): string {
  return [
    r.priorityPosition,
    r.obligationTypeCode,
    r.amountFormulaCode,
    r.recipientResolution,
  ].join("|");
}

function summarize(r: PriorityRow): string {
  const parts = [
    `#${r.priorityPosition}`,
    r.obligationTypeCode || "—",
    `→ ${r.recipientResolution || "—"}`,
    r.amountFormulaCode || "—",
  ];
  if (r.isTakeAllRemaining) parts.push("TAR");
  if (r.isPersistent) parts.push("persistent");
  return parts.join(" · ");
}

export function VersionDiff({ baseRows, proposedRows }: VersionDiffProps) {
  const baseKeys = new Set(baseRows.map(rowKey));
  const propKeys = new Set(proposedRows.map(rowKey));
  const max = Math.max(baseRows.length, proposedRows.length);

  const rows = Array.from({ length: max }).map((_, i) => {
    const base = baseRows[i];
    const prop = proposedRows[i];
    let kind: "same" | "modified" | "added" | "removed" = "same";
    if (base && !prop) kind = "removed";
    else if (!base && prop) kind = "added";
    else if (base && prop && (rowKey(base) !== rowKey(prop)
      || base.isPersistent !== prop.isPersistent
      || base.isTakeAllRemaining !== prop.isTakeAllRemaining
      || (base.firesOnIncomeTypesJson ?? "") !== (prop.firesOnIncomeTypesJson ?? "")
      || (base.conditionalLogicJson ?? "") !== (prop.conditionalLogicJson ?? ""))) {
      kind = "modified";
    }
    return { i, base, prop, kind };
  });

  const added = proposedRows.filter((p) => !baseKeys.has(rowKey(p))).length;
  const removed = baseRows.filter((b) => !propKeys.has(rowKey(b))).length;

  return (
    <div
      className="border border-border-light rounded-md p-space-3 bg-white-pure"
      data-testid="version-diff"
    >
      <h3 className="font-display font-bold text-h4 m-0 mb-space-1">
        Compared to active version
      </h3>
      <p className="text-caption text-gray-mid mb-space-2">
        <span data-testid="version-diff-added">{added} added</span> ·{" "}
        <span data-testid="version-diff-removed">{removed} removed</span>
      </p>
      <table className="w-full border-collapse text-small">
        <thead>
          <tr className="text-caption text-gray-mid uppercase">
            <th className="px-space-2 py-space-1 text-left">#</th>
            <th className="px-space-2 py-space-1 text-left">Active</th>
            <th className="px-space-2 py-space-1 text-left">Proposed</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ i, base, prop, kind }) => (
            <tr
              key={i}
              data-testid={`version-diff-row-${i}-${kind}`}
              className={cn(
                kind === "added" && "bg-status-success/10",
                kind === "removed" && "bg-status-error/10",
                kind === "modified" && "bg-status-warning/10",
              )}
            >
              <td className="px-space-2 py-space-1 font-mono">{i + 1}</td>
              <td className="px-space-2 py-space-1">
                {base ? summarize(base) : <em className="text-gray-mid">—</em>}
              </td>
              <td className="px-space-2 py-space-1">
                {prop ? summarize(prop) : <em className="text-gray-mid">—</em>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
